import React, { useState } from "react";
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  IconButton,
  useTheme,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  useMediaQuery,
  Collapse // Importálva a smooth animációhoz
} from "@mui/material";
import { FaChartLine, FaThumbsUp, FaThumbsDown, FaChevronDown, FaChevronUp } from "react-icons/fa";
import { Particles } from '../components/Particles.tsx';
import DashBoardNavBar from '../components/DashBoardNavBar.tsx';
import Navbar from '../components/Navbar.tsx';
import LeftPanel from '../components/LeftPanel.tsx';
import Cookies from 'js-cookie';

const API_BASE = `${process.env.REACT_APP_API_URL}/v1/content/public`;

interface ContentItem {
  id: number;
  title: string;
  category: string;
  url: string;
  views: number;
  up: number;
  down: number;
  createdAt: string;
  username: string;
  currentAvatar?: string;
}

const ContentLeaderboard: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isAuthenticated = !!Cookies.get('token');

  const [leftPanelOpen, setLeftPanelOpen] = useState(false);
  const [timeFrame, setTimeFrame] = useState<"weekly" | "monthly" | "all">("all");
  const [mostViewed, setMostViewed] = useState<ContentItem[]>([]);
  const [topRated, setTopRated] = useState<ContentItem[]>([]);
  const [lowRated, setLowRated] = useState<ContentItem[]>([]);
  
  const [expandedSections, setExpandedSections] = useState({
    mostViewed: false,
    topRated: false,
    lowRated: false
  });

  const [loadedSections, setLoadedSections] = useState({
    mostViewed: false,
    topRated: false,
    lowRated: false
  });

  const loadSectionData = async (sectionKey: keyof typeof loadedSections) => {
    try {
      const endpoints = {
        mostViewed: "toptenviewcontent",
        topRated: "toptenupratecontent",
        lowRated: "toptendownratecontent"
      };
      const response = await fetch(timeFrame === "all" 
        ? `${API_BASE}/${endpoints[sectionKey]}` 
        : `${process.env.REACT_APP_API_URL}/v1/content/public/${timeFrame}/${endpoints[sectionKey]}`);
      const data = await response.json();
      if (sectionKey === 'mostViewed') setMostViewed(data.slice(0, 10));
      if (sectionKey === 'topRated') setTopRated(data.slice(0, 10));
      if (sectionKey === 'lowRated') setLowRated(data.slice(0, 10));
      setLoadedSections(prev => ({ ...prev, [sectionKey]: true }));
    } catch (error) {
      console.error("API error:", error);
    }
  };

  const toggleExpand = (section: string) => {
    setExpandedSections(prev => {
      const sectionKey = section as keyof typeof prev;
      const newVal = !prev[sectionKey];
      const newState = { mostViewed: false, topRated: false, lowRated: false };
      newState[sectionKey] = newVal;
      if (newVal && !loadedSections[sectionKey as keyof typeof loadedSections]) {
        loadSectionData(sectionKey as keyof typeof loadedSections);
      }
      return newState;
    });
  };

  const marginLeft = isMobile ? 0 : (leftPanelOpen ? 240 : 56);
  const marginTop = '0px';

  const LeaderboardSection = ({ title, data, icon: Icon, valueKey, colorScheme, sectionKey }: any) => {
    const expanded = expandedSections[sectionKey as keyof typeof expandedSections];
    const isLoading = !loadedSections[sectionKey as keyof typeof loadedSections] && expanded;
    const boxShadowColor = colorScheme === "green" ? "#27ae60" : colorScheme === "red" ? "#c0392b" : "#bdc3c7";
    const cardBg = colorScheme === "green" ? "linear-gradient(135deg, #2ecc71, #27ae60)" : colorScheme === "red" ? "linear-gradient(135deg, #e74c3c, #c0392b)" : "linear-gradient(135deg, #ececec, #f5f5f5)";
    const textColor = colorScheme === "neutral" ? "#000" : "#fff";

    return (
      <Card sx={{ 
        mb: 2, 
        background: cardBg, 
        color: textColor, 
        boxShadow: `0 8px 16px 0px ${boxShadowColor}`, 
        borderRadius: 2,
        maxWidth: '100%',
        overflow: 'hidden',
        transition: 'transform 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)'
        }
      }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box 
            display="flex" 
            alignItems="center" 
            justifyContent="space-between" 
            sx={{ cursor: 'pointer' }}
            onClick={() => toggleExpand(sectionKey)}
          >
            <Box display="flex" alignItems="center">
              <Icon size={18} style={{ marginRight: "8px" }} />
              <Typography variant="h6" sx={{ fontFamily: "Orbitron, sans-serif", fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
                {title}
              </Typography>
            </Box>
            <IconButton size="small">
              {expanded ? <FaChevronUp color={textColor} /> : <FaChevronDown color={textColor} />}
            </IconButton>
          </Box>

          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ mt: 2 }}>
              {isLoading ? (
                <Box p={4} textAlign="center">
                  <CircularProgress size={24} color="inherit" />
                </Box>
              ) : (
                data.map((item: any, index: number) => (
                  <Box 
                    key={item.id} 
                    sx={{ 
                      p: 1, 
                      borderBottom: "1px solid rgba(255,255,255,0.2)", 
                      '&:last-child': { borderBottom: 'none' },
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' },
                      borderRadius: 1
                    }}
                  >
                    <Typography noWrap variant="subtitle2" sx={{ fontFamily: "Orbitron, sans-serif", fontSize: '0.8rem' }}>
                      {`${index + 1}. ${item.title}`}
                    </Typography>
                    <Typography variant="caption" display="block">
                      {valueKey === 'views' ? `Views: ${item.views.toLocaleString()}` : valueKey === 'up' ? `Up: ${item.up}` : `Down: ${item.down}`}
                    </Typography>
                    <Box display="flex" alignItems="center" mt={0.5} justifyContent="space-between">
                       <Typography variant="caption" sx={{ fontSize: '0.65rem', opacity: 0.8 }}>By: {item.username}</Typography>
                       <Avatar 
                         src={item.currentAvatar || `https://api.dicebear.com/7.x/identicon/svg?seed=${item.username}`} 
                         sx={{ width: 18, height: 18, border: "1px solid white" }} 
                       />
                    </Box>
                  </Box>
                ))
              )}
            </Box>
          </Collapse>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      width: '100vw', 
      overflowX: 'hidden', 
      backgroundColor: theme.palette.background.default,
      position: 'relative'
    }}>
      {isAuthenticated ? <DashBoardNavBar /> : <Navbar />}

      <LeftPanel
        isAuthenticated={isAuthenticated}
        onLoginRequired={() => navigate('/login')}
        onNavigation={(path) => navigate(path)}
        open={leftPanelOpen}
        onToggle={() => setLeftPanelOpen(prev => !prev)}
      />

      <Box sx={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", zIndex: 0, pointerEvents: 'none' }}>
        <Particles />
      </Box>

      <Container 
        component="main"
        maxWidth="lg"
        sx={{
          flexGrow: 1,
          zIndex: 1,
          paddingTop: marginTop,
          marginLeft: { xs: 0, sm: `${marginLeft}px` },
          width: { xs: '100%', sm: `calc(100% - ${marginLeft}px)` },
          maxWidth: { xs: '100%', sm: 'lg' }, 
          px: { xs: 1.5, sm: 3 }, 
          boxSizing: 'border-box',
          transition: 'all 0.3s ease',
        }}
      >
        <Typography variant="h4" align="center" sx={{ 
            fontFamily: "Orbitron, sans-serif", 
            my: 1,
            fontSize: { xs: '1.3rem', sm: '2rem' } 
        }}>
          LeaderBoard 🎯
        </Typography>

        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 140, bgcolor: 'background.paper', borderRadius: 1 }}>
            <InputLabel>Time Frame</InputLabel>
            <Select
              value={timeFrame}
              onChange={(e) => setTimeFrame(e.target.value as any)}
              label="Time Frame"
            >
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="all">All Time</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Grid container spacing={isMobile ? 1.5 : 3}>
          <Grid item xs={12} md={4}>
            <LeaderboardSection title="Most Viewed" data={mostViewed} icon={FaChartLine} valueKey="views" colorScheme="neutral" sectionKey="mostViewed" />
          </Grid>
          <Grid item xs={12} md={4}>
            <LeaderboardSection title="Top Rated" data={topRated} icon={FaThumbsUp} valueKey="up" colorScheme="green" sectionKey="topRated" />
          </Grid>
          <Grid item xs={12} md={4}>
            <LeaderboardSection title="Low Rated" data={lowRated} icon={FaThumbsDown} valueKey="down" colorScheme="red" sectionKey="lowRated" />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ContentLeaderboard;